import { core } from '../core/index.js';
import { filterFabricErrors } from '../errors/errors.js';

/**
 * 获取当前的地理位置、速度。开启高精度定位，接口耗时会增加，可指定 highAccuracyExpireTime 作为超时时间。
 * 地图相关使用的坐标格式应为 gcj02, 注意此处iOS使用内置地图引擎(非高德)
 */
export type FabricLocationOptions = {
  /**
   * wgs84 返回 gps 坐标，gcj02 返回可用于 wx.openLocation 的坐标
   * @default wgs84
   */
  type?: string;
  /**
   * 传入 true 会返回高度信息，由于获取高度需要较高精确度，会减慢接口返回速度
   */
  altitude?: boolean;
  /**
   * 开启高精度定位
   * @default false
   */
  isHighAccuracy?: boolean;
  /**
   * 定位超时时间(ms), 不提供则无限等待定位结果
   * @default 30000
   */
  expireTime?: number;
};

/**
 * 打开调试模式, 结果返回
 */
export type FabricLocationCallbackData = {
  /**
   * 纬度，范围为 -90~90，负数表示南纬
   */
  latitude: string;
  /**
   * 经度，范围为 -180~180，负数表示西经
   */
  longitude: string;
  /**
   * 速度，单位 m/s
   */
  speed: string;
  /**
   * 位置的精确度
   */
  accuracy: string;
  /**
   * 高度，单位 m
   */
  altitude: string;
  /**
   * 城市名称
   */
  cityName?: string;
};

export type FabricLocationGetLocationApi = (
  options: FabricLocationOptions
) => Promise<FabricLocationCallbackData>;

/**
 * 获取当前的地理位置、速度。开启高精度定位，接口耗时会增加，可指定 expireTime 作为超时时间
 * 1. 如果定位被用户强制关闭, 或者用户点了拒绝, 或者在设置里面禁用了, 返回错误code:`location_permission_disallow`
 * 2. 如果定位超时了, 则返回错误code: `location_service_timeout`
 * 3. 如果定位未决定, 则返回错误code: `location_service_not_determined`(iOS可能弹出来用户很久未点, 会自动抛出系统BUG)
 */
export const getLocation: FabricLocationGetLocationApi = ({
  type = 'wgs84',
  altitude,
  expireTime = 30000,
  isHighAccuracy = false,
}: FabricLocationOptions) => {
  return new Promise<FabricLocationCallbackData>((resolve, reject) => {
    core.call<
      'fabric_location.getLocation',
      FabricLocationOptions,
      FabricLocationCallbackData
    >(
      'fabric_location.getLocation',
      {
        type,
        altitude,
        expireTime,
        isHighAccuracy,
      },
      (result) => {
        const fabricErrors = filterFabricErrors(
          result.code,
          result.message,
          result.data
        );
        if (fabricErrors) {
          reject(fabricErrors);
        } else {
          resolve(result.data);
        }
      }
    );
  });
};
